/**
 * EV-010。エラーの記録（console.error・console.warn）から、アクセス用トークンなどの秘密を除く。
 *
 * axios のエラーは要求の設定（config.headers.Authorization: Bearer …）や送った要求そのもの
 * （request）を持つ。画面の各所は console.error(…, error) でそのまま記録しているため、短い期限の
 * アクセス用トークンがブラウザのコンソール・記録の収集先に残り得た（更新用トークンは HttpOnly の
 * Cookie で、スクリプトからは読めない。ADM-17）。
 *
 * 記録する前に、axios のエラーは原因を追える項目（文・code・状態・メソッド・URL・応答の本文）
 * だけにし、文字列の Bearer トークンと、名前がトークン・パスワードらしい項目を伏せる。
 *
 * エラーは Error のまま写し、stack（伏せたもの）を残す。以前は普通のオブジェクトに写して
 * stack が落ち、どこで失敗したかを追えなかった。console.log・info・debug も同じく包む
 * （画面に console.log(…, err) で記録する箇所があった）。
 */

const REDACTED = '[redacted]';
// YAC-2026-009 PR5a。2段階認証のチャレンジ（mfa_token）と、認証アプリの秘密（secret・otpauth_uri・qr_svg）も伏せる。
const SENSITIVE_FIELD = /^(authorization|access|refresh|token|access_token|refresh_token|password|cookie|set-cookie|x-csrftoken|mfa_token|secret|otpauth_uri|qr_svg)$/i;
const BEARER = /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g;
const MAX_DEPTH = 6;

function redactString(value: string): string {
  return value.replace(BEARER, `Bearer ${REDACTED}`);
}

/** name・message・stack を伏せて写した Error（コンソールで stack を追える）。 */
function copyError(error: Error): Error & Record<string, unknown> {
  const copy = new Error(redactString(String(error.message ?? ''))) as Error &
    Record<string, unknown>;
  copy.name = error.name;
  copy.stack = typeof error.stack === 'string' ? redactString(error.stack) : undefined;
  return copy;
}

function summarizeAxiosError(error: any, seen: WeakSet<object>, depth: number) {
  return Object.assign(copyError(error), {
    code: error.code,
    status: error.response?.status,
    method: error.config?.method,
    url: error.config?.url,
    data: redact(error.response?.data, seen, depth + 1),
  });
}

function redact(value: unknown, seen: WeakSet<object>, depth: number): unknown {
  if (typeof value === 'string') return redactString(value);
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value as object)) return '[circular]';
  if (depth > MAX_DEPTH) return '[…]';
  seen.add(value as object);
  const anyValue = value as any;
  if (anyValue.isAxiosError) return summarizeAxiosError(anyValue, seen, depth);
  if (value instanceof Error) {
    const copy = copyError(value);
    Object.keys(value).forEach((key) => {
      copy[key] = SENSITIVE_FIELD.test(key) ? REDACTED : redact(anyValue[key], seen, depth + 1);
    });
    return copy;
  }
  if (Array.isArray(value)) return value.map((item) => redact(item, seen, depth + 1));
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value; // DOM の要素など、そのまま
  const copy: Record<string, unknown> = {};
  Object.keys(value).forEach((key) => {
    copy[key] = SENSITIVE_FIELD.test(key) ? REDACTED : redact(anyValue[key], seen, depth + 1);
  });
  return copy;
}

/** 記録する値から秘密を除いた写しを返す（元の値は変えない）。 */
export function redactForLog(value: unknown): unknown {
  return redact(value, new WeakSet(), 0);
}

/** console の記録（error・warn・log・info・debug）が、秘密を除いた値を記録するようにする。 */
export function installRedactingConsole(target: Console = console): void {
  (['error', 'warn', 'log', 'info', 'debug'] as const).forEach((level) => {
    const original = target[level];
    if (typeof original !== 'function' || (original as any).__redacting) return;
    const wrapped = (...args: unknown[]) => original.apply(target, args.map(redactForLog));
    (wrapped as any).__redacting = true;
    target[level] = wrapped as any;
  });
}
